import {
  ChartCalculationType,
  ChartInterval,
  type chartLayoutFragmentFragment,
  ChartTypes,
  type GetChartLayoutQuery,
  type IChartAggregation,
  type IChartXAxisType,
  type IChartYAxisType,
  ValueDisplayMethod,
} from "@/graphql";
import type { PartialBy } from "@/typeDefinitions/custom/PartialBy";
import type _Highcharts from "highcharts/highcharts-more";

export const FIXED_COLUMN_PADDING_DEFAULT = 0.5;
export const FIXED_COLUMN_PADDING = [
  0.1, 0.324, 0.37328, 0.4087616, 0.434308352, 0.452702013, 0.46594545, 0.475480724, 0.482346121,
  0.487289207, 0.490848229, 0.493410725, 0.495255722, 0.49658412, 0.497540566,
];

export type SeriesTypeOption =
  | _Highcharts.SeriesAreaOptions
  | Highcharts.SeriesAreasplineOptions
  | Highcharts.SeriesColumnOptions
  | Highcharts.SeriesLineOptions
  | _Highcharts.SeriesSplineOptions
  | _Highcharts.SeriesArearangeOptions
  | Highcharts.SeriesAreasplinerangeOptions
  | Highcharts.SeriesColumnrangeOptions
  | _Highcharts.SeriesPieOptions;

/**
 * This array is to determine which interval is before others
 */
export const ChartIntervalOrder = [
  ChartInterval.SECOND,
  ChartInterval.MINUTE,
  ChartInterval.HOUR,
  ChartInterval.SHIFT,
  ChartInterval.DAY,
  ChartInterval.WEEK,
  ChartInterval.MONTH,
  ChartInterval.YEAR,
  ChartInterval.BUSINESS_YEAR,
] as const;

export type ChartDatapointSeriesType = ChartEditType["datapoints"][number];
export type ChartTargetSeriesType = ChartEditType["targets"][number];
export type ChartCalculationSeriesType = ChartEditType["calculations"][number];

export type ChartSeriesType =
  | ChartDatapointSeriesType
  | ChartTargetSeriesType
  | ChartCalculationSeriesType;

export type ChartCommonAxisType = IChartXAxisType | IChartYAxisType;

export type ChartLayoutCommonTypeWithData<T> = T & {
  commonType: { chartSeriesData: { data: unknown } };
};

export type ChartObjectType = Record<
  ChartTypes,
  "preDefinedCharts" | "customCharts" | "losePreDefined"
>;

export function isCostCenterChart(chartType: ChartTypes): boolean {
  return [ChartTypes.customCategory].includes(chartType);
}

export const chartLayoutCategories: ChartObjectType = {
  [ChartTypes.target]: "preDefinedCharts",
  [ChartTypes.feedback]: "preDefinedCharts",
  [ChartTypes.pps]: "preDefinedCharts",
  [ChartTypes.custom]: "customCharts",
  [ChartTypes.customCategory]: "customCharts",
};

export enum ChartCalculationTypeAddition {
  CUMULATION = "CUMULATION",
  MOVINGAVERAGE = "MOVINGAVERAGE",
}

export enum SeriesModalViewTypes {
  datapoint = "datapoint",
  calculation = "calculation",
  target = "target",
}

export type ChartAddEditCommonType = {
  key?: string;
  name: string;
  aggregation: IChartAggregation;
  valueDisplayMethod: ValueDisplayMethod;
  dataLabelsEnabled?: boolean;
  maxCalculationDateToday: boolean;
};

export type ChartAddEditDatapointType = ChartAddEditCommonType & {
  key?: string;
  name: string;
  datapointTypeId?: number;
  costCenterId?: number;
  interpolateDatapoints?: boolean;
  dataManipulationInterval?: ChartInterval;
};

export type ChartAddEditTargetType = ChartAddEditCommonType & {
  datapointTypeId?: number;
  isRange: boolean;
  costCenterId?: number;
};

export type ChartAddEditCalculationType = ChartAddEditCommonType & {
  calculationKeys: {
    key: string;
    fixValue?: number;
    aggregation?: ChartCalculationType;
  }[];
  unit: string;
  requireAllDataCalculationKeys: boolean;
};

export type ChartAddEditType =
  | ChartAddEditDatapointType
  | ChartAddEditTargetType
  | ChartAddEditCalculationType;

export type ChartEditSummaryType = Omit<
  NonNullable<GetChartLayoutQuery["ChartLayouts"][0]["summary"]>,
  "dataSeriesKey" | "dataTargetSeriesKey"
> & {
  datapointSeriesKey?: string;
  targetSeriesKey?: string;
};

export type ChartEditType = Omit<
  PartialBy<GetChartLayoutQuery["ChartLayouts"][0], "dataGranularity">,
  "defaultCostCenterId" | "datapoints" | "usage" | "id" | "summary"
> & {
  id?: number;
  defaultCostCenterId?: number;
  datapoints: (Omit<GetChartLayoutQuery["ChartLayouts"][0]["datapoints"][number], "ccConn"> & {
    ccConn: (Omit<
      GetChartLayoutQuery["ChartLayouts"][0]["datapoints"][number]["ccConn"][number],
      "costCenterId"
    > & {
      costCenterId?: number;
    })[];
  })[];
  summary?: ChartEditSummaryType;
};

export type ChartSettingsForTileType = Omit<chartLayoutFragmentFragment, "id">;

export function convertChartEditTypeToChartSettingsForTileType(chartSettings: ChartEditType) {
  if (
    chartSettings.defaultCostCenterId == null ||
    chartSettings.dataGranularity == null ||
    chartSettings.datapoints.some((datapoint) =>
      datapoint.ccConn.some((costCenter) => costCenter.costCenterId == null),
    )
  ) {
    return null;
  }
  const converter = {
    ...chartSettings,
    defaultCostCenterId: chartSettings.defaultCostCenterId as number,
    dataGranularity: chartSettings.dataGranularity as ChartInterval,
    datapoints: chartSettings.datapoints as GetChartLayoutQuery["ChartLayouts"][0]["datapoints"],
  };
  const settingsForTileType: ChartSettingsForTileType = converter;

  return settingsForTileType;
}

export default {};
